import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';
import CustomerCreationForm from '../components/CustomerCreationForm';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  createJarvisContact,
  deleteJarvisContact,
  listJarvisContacts,
  promoteJarvisContact,
  updateJarvisContact,
} from '../elevate/contacts.client';
import { findCustomersByPhone, type DirectoryCustomerMatch } from '../elevate/identity';
import { loadIdentityIndex } from '../elevate/identity.client';
import { matchesContactSearch, type ContactDraft, type JarvisContact } from '../elevate/contacts.model';
import { customerHref } from '../elevate/phone';

export const emptyContactDraft = (): ContactDraft => ({
  firstName: '',
  lastName: '',
  company: '',
  email: '',
  phone: '',
  landline: '',
  internalNotes: '',
  displayName: '',
});

export function draftFromContact(contact: JarvisContact): ContactDraft {
  return {
    firstName: contact.firstName || '',
    lastName: contact.lastName || '',
    company: contact.company || '',
    email: contact.email || '',
    phone: contact.phoneOriginal || '',
    landline: contact.landlineOriginal || '',
    internalNotes: contact.internalNotes || '',
    displayName: contact.displayName || '',
  };
}

export const ContactEditorFields: React.FC<{
  draft: ContactDraft;
  setDraft: React.Dispatch<React.SetStateAction<ContactDraft>>;
  originalSuggestion?: string | null;
}> = ({ draft, setDraft, originalSuggestion }) => (
  <Stack gap={1.25}>
    {originalSuggestion && (
      <Alert color="neutral">
        Caller name as received: {originalSuggestion}. Edit if needed.
      </Alert>
    )}
    <FormControl>
      <FormLabel>Name</FormLabel>
      <Input
        value={draft.displayName || `${draft.firstName} ${draft.lastName}`.trim()}
        onChange={(event) => {
          const value = event.target.value;
          const parts = value.trim().split(/\s+/);
          setDraft((current) => ({
            ...current,
            displayName: value,
            firstName: parts[0] || '',
            lastName: parts.slice(1).join(' '),
          }));
        }}
      />
    </FormControl>
    <FormControl>
      <FormLabel>Telephone</FormLabel>
      <Input value={draft.phone} onChange={(event) => setDraft((current) => ({ ...current, phone: event.target.value }))} />
    </FormControl>
  </Stack>
);

const ContactsPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [rows, setRows] = useState<JarvisContact[]>([]);
  const [customers, setCustomers] = useState<DirectoryCustomerMatch[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<JarvisContact | null>(null);
  const [draft, setDraft] = useState<ContactDraft>(emptyContactDraft());
  const [pendingDelete, setPendingDelete] = useState<JarvisContact | null>(null);
  const [promote, setPromote] = useState<JarvisContact | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [contacts, index] = await Promise.all([listJarvisContacts(''), loadIdentityIndex()]);
      setRows(contacts);
      setCustomers(index.customers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => rows.filter((row) => matchesContactSearch(row, query)), [rows, query]);

  const linkedCustomer = (contact: JarvisContact) => (
    contact.customerId ? customers.find((row) => row.id === contact.customerId) : undefined
  );

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyContactDraft());
    setFormOpen(true);
  };

  const openEdit = (contact: JarvisContact) => {
    setEditing(contact);
    setDraft(draftFromContact(contact));
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editing) await updateJarvisContact(editing.id, draft);
      else await createJarvisContact(draft);
      setFormOpen(false);
      setNotice(editing ? 'Contact updated.' : 'Contact saved. Matching recordings will show this name.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save contact');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setSaving(true);
    setError(null);
    try {
      await deleteJarvisContact(pendingDelete.id);
      setPendingDelete(null);
      setNotice('Contact removed. Recordings were kept.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete contact');
    } finally {
      setSaving(false);
    }
  };

  const startPromote = async (contact: JarvisContact) => {
    const index = await loadIdentityIndex();
    const existing = findCustomersByPhone(index.customers, contact.phoneOriginal || contact.phoneNormalized);
    if (existing[0]) {
      try {
        await promoteJarvisContact(contact.id, existing[0].id);
        setNotice(`${existing[0].name} is already in the Directory, so the contact was removed from Contacts.`);
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not link this contact');
      }
      return;
    }
    setPromote(contact);
  };

  const finishPromote = async (customer: { id: string; name?: string }) => {
    if (!promote) return;
    setSaving(true);
    try {
      await promoteJarvisContact(promote.id, customer.id);
      setPromote(null);
      setNotice(`${customer.name || promote.displayName} is now a Directory customer and was removed from Contacts.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not promote contact');
    } finally {
      setSaving(false);
    }
  };

  const statusChip = (contact: JarvisContact) => {
    const customer = linkedCustomer(contact);
    if (customer || contact.status === 'promoted') {
      return <Chip size="sm" color="primary" variant="soft">Directory Customer</Chip>;
    }
    return <Chip size="sm" variant="soft">Contact</Chip>;
  };

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, pb: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography level="h2">Contacts</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Lightweight identities used to name callers. Directory remains the full CRM.
          </Typography>
        </Box>
        <Button startDecorator={<IconWrapper icon={FaPlus} />} onClick={openCreate}>
          Add Contact
        </Button>
      </Stack>

      {notice && <Alert color="success" sx={{ mb: 2 }}>{notice}</Alert>}
      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

      <Input
        placeholder="Search name or telephone"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        sx={{ mb: 2 }}
      />

      {loading && rows.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && visible.length === 0 && !error && (
        <Alert color="neutral">No contacts yet. Add one here or from a call recording.</Alert>
      )}

      {visible.length > 0 && isMobile && (
        <Stack gap={1.5}>
          {visible.map((contact) => {
            const customer = linkedCustomer(contact);
            return (
              <Card key={contact.id} variant="outlined">
                <CardContent>
                  {customer ? (
                    <Typography
                      component={RouterLink}
                      to={customerHref(customer.name)}
                      level="title-sm"
                      sx={{ color: 'primary.600', textDecoration: 'none' }}
                    >
                      {customer.name}
                    </Typography>
                  ) : (
                    <Typography level="title-sm" onClick={() => openEdit(contact)} sx={{ cursor: 'pointer' }}>
                      {contact.displayName}
                    </Typography>
                  )}
                  <Typography level="body-sm">{contact.phoneOriginal || contact.landlineOriginal || '—'}</Typography>
                  <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }}>{statusChip(contact)}</Stack>
                  {!customer && contact.status !== 'promoted' && (
                    <Button size="sm" variant="plain" sx={{ mt: 1 }} onClick={() => void startPromote(contact)}>
                      Promote to Directory
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {visible.length > 0 && !isMobile && (
        <Table stripe="even" hoverRow>
          <thead>
            <tr>
              <th>Name</th>
              <th>Telephone</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((contact) => {
              const customer = linkedCustomer(contact);
              return (
                <tr key={contact.id}>
                  <td>
                    {customer ? (
                      <Typography
                        component={RouterLink}
                        to={customerHref(customer.name)}
                        level="body-sm"
                        sx={{ color: 'primary.600', textDecoration: 'none', fontWeight: 600 }}
                      >
                        {customer.name}
                      </Typography>
                    ) : (
                      <Typography level="body-sm" onClick={() => openEdit(contact)} sx={{ cursor: 'pointer', fontWeight: 600 }}>
                        {contact.displayName}
                      </Typography>
                    )}
                  </td>
                  <td>{contact.phoneOriginal || contact.landlineOriginal || '—'}</td>
                  <td>{statusChip(contact)}</td>
                  <td>
                    <Stack direction="row" justifyContent="flex-end">
                      {!customer && contact.status !== 'promoted' && (
                        <Button size="sm" variant="plain" onClick={() => void startPromote(contact)}>
                          Promote to Directory
                        </Button>
                      )}
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        aria-label={`Delete ${contact.displayName}`}
                        onClick={() => setPendingDelete(contact)}
                      >
                        <IconWrapper icon={FaTrash} />
                      </IconButton>
                    </Stack>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Modal open={formOpen} onClose={() => setFormOpen(false)}>
        <ModalDialog sx={{ width: 420, maxWidth: 'calc(100vw - 24px)' }}>
          <DialogTitle>{editing ? 'Edit contact' : 'Add Contact'}</DialogTitle>
          <DialogContent>
            <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 1.5 }}>
              Name and telephone only. Promote later if this person should become a Directory customer.
            </Typography>
            <ContactEditorFields draft={draft} setDraft={setDraft} />
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={() => void save()}>{editing ? 'Save' : 'Save contact'}</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={!!pendingDelete} onClose={() => setPendingDelete(null)}>
        <ModalDialog>
          <DialogTitle>Remove contact?</DialogTitle>
          <DialogContent>
            This removes {pendingDelete?.displayName} from Contacts. Call recordings stay. Directory customers are not deleted.
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button color="danger" loading={saving} onClick={() => void confirmDelete()}>Remove</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={!!promote} onClose={() => setPromote(null)}>
        <ModalDialog sx={{ width: 640, maxWidth: 'calc(100vw - 24px)', maxHeight: '90vh', overflow: 'auto' }}>
          <DialogTitle>Promote to Directory</DialogTitle>
          <DialogContent>
            {promote && (
              <CustomerCreationForm
                compact
                initialData={{
                  first_name: promote.firstName || promote.displayName.split(/\s+/)[0] || '',
                  last_name: promote.lastName || promote.displayName.split(/\s+/).slice(1).join(' ') || '',
                  phone_number: promote.phoneOriginal || '',
                  landline_number: promote.landlineOriginal || '',
                  email: promote.email || '',
                }}
                onCancel={() => setPromote(null)}
                onCustomerCreated={(customer) => void finishPromote(customer)}
              />
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default ContactsPage;
